import { inject, Injectable, signal } from '@angular/core';
import { from, map, Observable, tap } from 'rxjs';
import { SupabaseService } from './supabase.service';
import { Notification } from '../models/interfaces';
import { NotificationCategory, NotificationModule, Tables } from '../models/enums';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private supabase = inject(SupabaseService);

  notifications = signal<Notification[]>([]);
  unreadCount = signal<number>(0);
  //isLoading = signal<boolean>(false);

  private readonly selectQuery = `
    *,
    emitter:profiles!notifications_emitter_id_fkey(id, first_name, last_name, photo_url)
  `;

  getNotifications(limit = 10, offset = 0): Observable<Notification[]> {
    return from(
      this.supabase.client
        .from(Tables.Notifications)
        .select(this.selectQuery)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)
    ).pipe(
      map(response => {
        if (response.error) throw response.error;
        return response.data as Notification[];
      }),
      tap(notifications => {
        if (offset === 0) {
          this.notifications.set(notifications);
        } else {
          this.notifications.update(current => [...current, ...notifications]);
        }
       //this.isLoading.set(false);
      })
    );
  }

  getNotificationsWithPagination(
    page: number,
    perPage = 20,
    filters?: NotificationListFilters
  ): Observable<{ total: number; data: Notification[] }> {
    let query = this.supabase.client
      .from(Tables.Notifications)
      .select(this.selectQuery, { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters?.categories?.length) query = query.in('category', filters.categories);
    if (filters?.modules?.length) query = query.in('module', filters.modules);
    if (filters?.isRead !== undefined && filters.isRead !== null) query = query.eq('is_read', filters.isRead);
    // Recherche unifiée (MOKA-363) : titre ou contenu, sans les caractères qui casseraient
    // le filtre PostgREST `or` (virgules, parenthèses) ni les jokers saisis.
    const term = notificationSearchTerm(filters?.search);
    if (term) query = query.or(`title.ilike.*${term}*,content.ilike.*${term}*`);

    query = query.range((page - 1) * perPage, page * perPage - 1);

    return from(query).pipe(
      map(response => {
        if (response.error) throw response.error;
        return {
          total: response.count ?? 0,
          data: response.data as Notification[]
        };
      })
    );
  }

  /** Repasse des notifications en non lues (sélection de la page, MOKA-363). */
  markAsUnread(notificationIds: number[]): Observable<void> {
    return from(
      this.supabase.client.rpc('mark_notifications_as_unread', { p_notification_ids: notificationIds })
    ).pipe(
      map(response => {
        if (response.error) throw response.error;
        this.notifications.update(list =>
          list.map(n => notificationIds.includes(n.id as number) ? { ...n, is_read: false } : n)
        );
      })
    );
  }

  /** Supprime plusieurs notifications de l'utilisateur (RLS : delete own). */
  deleteNotifications(notificationIds: number[]): Observable<void> {
    return from(
      this.supabase.client.from(Tables.Notifications).delete().in('id', notificationIds)
    ).pipe(
      map(response => {
        if (response.error) throw response.error;
        this.notifications.update(list => list.filter(n => !notificationIds.includes(n.id as number)));
      })
    );
  }

  getUnreadCount(): Observable<number> {
    return from(
      this.supabase.client
        .from(Tables.Notifications)
        .select('id', { count: 'exact', head: true })
        .eq('is_read', false)
    ).pipe(
      map(response => {
        if (response.error) throw response.error;
        const count = response.count ?? 0;
        this.unreadCount.set(count);
        return count;
      })
    );
  }

  markAsRead(notificationIds: number[]): Observable<void> {
    return from(
      this.supabase.client.rpc('mark_notifications_as_read', {
        p_notification_ids: notificationIds
      })
    ).pipe(
      map(response => {
        if (response.error) throw response.error;
        this.notifications.update(notifications =>
          notifications.map(n =>
            notificationIds.includes(n.id as number)
              ? { ...n, is_read: true }
              : n
          )
        );
        this.unreadCount.update(count => Math.max(0, count - notificationIds.length));
      })
    );
  }

  /**
   * Marque comme lues TOUTES les notifications de l'utilisateur, pas seulement
   * celles chargées en mémoire : l'ancienne version collectait les ids des
   * notifications du signal (les 10 de la popover) et laissait le badge > 0
   * quand il en restait de plus anciennes. Le RPC fait l'update global côté
   * serveur (aucune policy UPDATE n'existe pour authenticated).
   */
  markAllAsRead(): Observable<void> {
    return from(
      this.supabase.client.rpc('mark_all_my_notifications_as_read')
    ).pipe(
      map(response => {
        if (response.error) throw response.error;
        this.notifications.update(notifications =>
          notifications.map(n => (n.is_read ? n : { ...n, is_read: true }))
        );
        this.unreadCount.set(0);
      })
    );
  }

  deleteNotification(id: number): Observable<boolean> {
    return from(
      this.supabase.client
        .from(Tables.Notifications)
        .delete()
        .eq('id', id)
    ).pipe(
      map(response => {
        if (response.error) throw response.error;
        const wasUnread = this.notifications().some(n => n.id === id && !n.is_read);
        this.notifications.update(notifications =>
          notifications.filter(n => n.id !== id)
        );
        if (wasUnread) {
          this.unreadCount.update(count => Math.max(0, count - 1));
        }
        return true;
      })
    );
  }

  deleteAllNotifications(): Observable<void> {
    return from(
      this.supabase.client.rpc('delete_all_my_notifications')
    ).pipe(
      map(response => {
        if (response.error) throw response.error;
        this.notifications.set([]);
        this.unreadCount.set(0);
      })
    );
  }

  refreshNotifications(): void {
    this.getNotifications(10, 0).subscribe();
    this.getUnreadCount().subscribe();
  }
}

/** Filtres de la page des notifications (MOKA-363) : plusieurs modules et catégories, recherche. */
export interface NotificationListFilters {
  modules?: NotificationModule[];
  categories?: NotificationCategory[];
  isRead?: boolean | null;
  search?: string | null;
}

/** Terme de recherche nettoyé pour un filtre PostgREST `ilike` dans un `or(...)`. */
export function notificationSearchTerm(value: string | null | undefined): string {
  return (value ?? '').replace(/[,()*%"\\]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80);
}
